import dayjs, { ManipulateType } from 'dayjs';
import { StringKeyObject } from '@/types';
import { userStore } from '@/store';
import { DUMMY_IMG } from '@/configs';

/**
 * 各種文字列加工を行うユーティリティ郡
 * @module utils/filter
 */
export const now = (format: string): string => {
  const value = dayjs().format();

  if (typeof format !== 'string') {
    format = 'YYYY-MM-DD HH:mm';
  }
  return dayjs(value).format(format);
};

/**
 * 日付のフォーマット
 *
 * @param {(string | null)} value 対象の文字列
 * @param {string} [format] フォーマット指定
 * @returns {string} フォーマットされた文字列
 */
export const formatDate = (value: string | null, format?: string): string => {
  if (value == null || value === '') {
    return '';
  }
  if (typeof format !== 'string') {
    format = 'YYYY-MM-DD';
  }
  return dayjs(value).format(format);
};

/**
 * 加算した日付のフォーマット
 *
 * @param {(string | null)} value 対象の文字列
 * @param {number} [addition] 追加数
 * @param {ManipulateType} [unit] 加算する単位
 * @param {string} [format] フォーマット指定
 * @returns {string} フォーマットされた文字列
 */
export const addDateformat = (
  value: string | null = 'now',
  addition = 1,
  unit: ManipulateType = 'M',
  format?: string
): string => {
  if (value == null || value === '') {
    return '';
  }

  if (typeof format !== 'string') {
    format = 'YYYY-MM-DD';
  }

  const date = value === 'now' ? dayjs(now(format)) : dayjs(value);
  return date.add(addition, unit).format(format);
};

/**
 * 日付の比較
 *
 * @param {(string | null)} comparison 対象の文字列
 * @param {(string | null)} value 対象の文字列
 * @param {string} [operator] 加算する単位
 * @param {boolean} isOnlyDay 日付のみでの比較を行うかどうか
 * @returns {boolean} 比較結果
 */
export const compareDate = (
  comparison: string | null,
  value: string | null,
  operator: '>' | '<' | '>=' | '<=' = '<',
  isOnlyDay?: boolean
): boolean => {
  if (value == null || value === '') {
    return false;
  }
  if (comparison == null || comparison === '') {
    return false;
  }

  // 日時での比較の場合正確でない場合があるため、その場合は`isOnlyDay`を用いて日付で比較する
  const instance = isOnlyDay
    ? dayjs(value).startOf('day')
    : dayjs(value);
  const comparisonInstance = isOnlyDay
    ? dayjs(comparison).startOf('day')
    : dayjs(comparison);

  switch (operator) {
    case '<':
      return instance.isBefore(comparisonInstance);
    case '>':
      return instance.isAfter(comparisonInstance);
    case '<=':
      return instance.isSame(comparisonInstance) || instance.isBefore(comparisonInstance);
    case '>=':
      return instance.isSame(comparisonInstance) || instance.isAfter(comparisonInstance);
    default:
      return false;
  }
};

/**
 * 日時のフォーマット
 *
 * @param {(string | null)} value 対象の文字列
 * @param {string} [format] フォーマット指定
 * @returns {string} フォーマットされた文字列
 */
export const formatDateTime = (
  value: string | null,
  format?: string
): string => {
  if (value == null || value === '') {
    return '';
  }
  if (typeof format !== 'string') {
    format = 'YYYY-MM-DD HH:mm';
  }
  return dayjs(value).format(format);
};

/**
 * 時間のフォーマット
 *
 * @param {(string | null)} value 対象の文字列
 * @param {string} [format] フォーマット指定
 * @returns {string} フォーマットされた文字列
 */
export const formatTime = (
  value: string | null,
  format?: string
): string => {
  if (value == null || value === '') {
    return '';
  }
  if (typeof format !== 'string') {
    format = 'HH:mm';
  }
  return dayjs(value).format(format);
};

/**
 * 文字列の長さ加工
 *
 * @param {any} value 対象文字列
 * @param {number} [specified_length] 返却する文字数
 * @param {string} [specified_omission] Suffix文字列
 * @returns {string} フォーマットされた文字列
 */
export const truncate = (
  // eslint-disable-next-line
  value: any,
  // eslint-disable-next-line
  specified_length?: any,
  specified_omission?: string
): string => {
  const length = specified_length ? parseInt(specified_length, 10) : 20;
  const ommision = specified_omission ? specified_omission.toString() : '...';
  if (value === undefined || value === null) {
    value = '';
  }

  if (value.length <= length) {
    return value;
  }

  return value.substring(0, length) + ommision;
};

/**
 * 数値のフォーマット
 *
 * @param {(string | null)} value 対象の文字列
 * @returns {string} フォーマットされた文字列
 */
export const formatNumber = (value: string): string => {
  if (value === undefined || value === null) {
    value = '';
  }
  if (!value) {
    value = '0';
  }
  return Number(value).toLocaleString();
};

/**
 * ログインユーザーの必須権限チェック TODO:API連携後にテストを書く
 *
 * @param {string[]} required 必須権限配列
 * @return {*}  {boolean}
 */
export const requirePermission = (required: string[]): boolean => {
  const permittedActions = userStore.permittedActions;

  if (userStore.loginUser && userStore.loginUser.user_is_super_user) {
    return true;
  }

  if (!required.length || required.length === 0) {
    return false;
  }

  if (!Array.isArray(permittedActions)) {
    console.log('invalid permittedActions');
    return false;
  }

  return required.every(action => {
    return permittedActions.includes(action);
  });
};

/**
 * ログインユーザーのいづれかの権限チェック TODO:API連携後にテストを書く
 *
 * @param {[]} any 権限配列
 * @return {*}  {boolean}
 */
export const anyPermission = (any: []): boolean => {
  const permittedActions = userStore.permittedActions;

  if (userStore.loginUser && userStore.loginUser.user_is_super_user) {
    return true;
  }

  if (!any.length || any.length === 0) {
    return false;
  }

  if (!Array.isArray(permittedActions)) {
    console.log('invalid permittedActions');
    return false;
  }

  return any.some(action => {
    return permittedActions.includes(action);
  });
};

/**
 * ログインユーザーのチャット必須権限チェック TODO:API連携後にテストを書く
 *
 * @param {[]} required 必須権限配列
 * @return {*}  {boolean}
 */
export const requireChatPermission = (required: []): boolean => {
  const permittedChatActions = userStore.permittedChatActions;

  if (userStore.loginUser && userStore.loginUser.user_is_super_user) {
    return true;
  }

  if (!required.length || required.length === 0) {
    return false;
  }

  if (!Array.isArray(permittedChatActions)) {
    console.log('invalid permittedActions');
    return false;
  }

  return required.every(action => {
    return permittedChatActions.includes(action);
  });
};

/**
 * 配列要素を文字列により連結した文字列を返す
 *
 * @param {*} value
 * @param {string} delimiter 結合する文字列
 * @return {*}  {string}
 */
export const implode = (value: string | null | []): any => {
  if (value === undefined || value === null) {
    return '';
  }

  let returnValue = value;

  if (Array.isArray(value)) {
    returnValue = value.join(', ');
  }

  return returnValue;
};

/**
 * 発言中の特殊記号をサニタイジングする
 *
 * @param {any | null | []} value 入力文字列
 * @return {*}  {string}
 */
export const formatChatRemark = (value: any | null | []): any => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const entities: StringKeyObject = {
    '&': '&amp;',
    "'": '&apos;',
    '"': '&quot;',
    '<': '&lt;',
    '>': '&gt;'
  };

  const regex = /((https?|ftp)(:\/\/[-_.!~*'()a-zA-Z0-9;/?:@&=+$,%#]+))/g;
  const replacement = '<a target="_blank" class="replacement-link" href="$1">$1</a>';

  const quote_regex_start = /\[quote\]/g;
  const quote_regex_end = /\[\/quote\]/g;
  const quote_replacement_start = '<div class="quote">';
  const quote_replacement_end = '</div>';

  return (value || '')
    .replace(/[&'"<>]/g, (match: string) => entities[match])
    .replace(/\r?\n/g, '<br>')
    .replace(regex, replacement)
    .replace(/\[@[-a-z0-9]+\]/g, '<div style="display: inline-flex;"><span class="mention-badge">TO</span></div>')
    .replace(/\[RE:[-a-z0-9]+\]/g, '<div style="display: inline-flex;"><span class="reply-badge"><i class="fas fa-reply"></i>RE</span></div>')
    .replace(quote_regex_start, quote_replacement_start)
    .replace(quote_regex_end, quote_replacement_end);
};

/**
 * リンク文字列をhtmlリンクに変換
 *
 * @param {*} value 対象リンク文字列
 * @returns {string} 変換された文字列
 */
export const linkify = (value: string): string => {
  try {
    let replacedText;

    const replacePattern1 = /(\b(https?):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim;
    replacedText = value.replace(replacePattern1, '<a href="$1" target="_blank">$1</a>');

    const replacePattern2 = /(^|[^/])(www\.[\S]+(\b|$))/gim;
    replacedText = replacedText.replace(replacePattern2, '$1<a href="http://$2" target="_blank">$2</a>');

    const replacePattern3 = /(([a-zA-Z0-9\-_.])+@[a-zA-Z_]+?(\.[a-zA-Z]{2,6})+)/gim;
    return replacedText.replace(replacePattern3, '<a href="mailto:$1">$1</a>');
  } catch (e) {
    return value;
  }
};

/**
 * 改行文字の前に HTML の改行タグを挿入する
 *
 * @param {*} value 対象文字列
 * @returns {string} 変換された文字列
 */
export const nl2br = (value: string): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  value = value.replace(/¥n/g, '<br>');
  return value.replace(/\r?\n/g, '<br>');
};

/**
 * 環境相違により画像が取得できない場合はダミー画像のパスを返す
 *
 * @param url
 */
export const imgUrl = (url: string): string => {
  // 本番は置換処理は行わないためそのまま返す
  if (process.env.NAME === 'PRODUCTION') {
    return url;
  }

  // `url`の文字列がURLの形式でない場合は、以下のエラーが発生
  // Uncaught TypeError: Failed to construct 'URL': Invalid URL
  try {
    const urlObj: URL = new URL(url);
    const urlEndpoint = urlObj.origin;

    // 画像ファイルのURLのエンドポイントが本番環境のファイルパスのエンドポイントと一致する（本番データ画像の場合はダミーを返す）
    if (urlEndpoint === self.$nuxt.context.$config.s3Endpoint) {
      url = DUMMY_IMG;
    }
  } catch (_) {
    console.log('invalid url : ' + url);
  }
  return url;
};

// 拡張子 → OS/ブラウザ環境で変動しうる想定MIMEの候補
// Windows で HEIF Image Extensions 未導入時などに file.type が空文字になるケースを救済
// RFC 2045 §5.1 に従い MIME は case-insensitive で比較するため、値は lowercase で定義する
const EXTENSION_FALLBACK_MIMES: Record<string, readonly string[]> = {
  xlsm: [
    'application/vnd.ms-excel.sheet.macroenabled.12',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ],
  heic: ['image/heic', 'image/heif'],
  ods: ['application/vnd.oasis.opendocument.spreadsheet'],
  odt: ['application/vnd.oasis.opendocument.text'],
  numbers: ['application/x-iwork-numbers-sffnumbers'],
  psd: ['application/x-photoshop', 'image/vnd.adobe.photoshop'],
  xdw: ['application/vnd.fujixerox.docuworks'],
  wmv: ['video/x-ms-wmv'],
  wav: ['audio/wav', 'audio/x-wav'],
  eml: ['message/rfc822']
};

type ValidateFileTypeReport = {
  fileType: string;
  fileName?: string;
  extension: string;
  accept: string;
  fallbackMatched: boolean;
};

const reportValidateFileTypeFallback = (ctx: ValidateFileTypeReport): void => {
  if (typeof self === 'undefined') {
    return;
  }
  const sentry = (self as any).$nuxt?.$sentry;
  if (!sentry?.captureMessage) {
    return;
  }
  try {
    sentry.captureMessage('validateFileType: fallback path evaluated', {
      level: ctx.fallbackMatched ? 'info' : 'warning',
      extra: {
        fileType: ctx.fileType || '<empty>',
        fileName: ctx.fileName ?? '<none>',
        extension: ctx.extension,
        acceptSample: ctx.accept.slice(0, 200),
        fallbackMatched: ctx.fallbackMatched,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '<no-navigator>'
      }
    });
  } catch (e) {
    // Sentry 送信自体が失敗してもファイル検証ロジックを壊さない
    // ただし原因追跡のため console.warn で観測可能にしておく
    // eslint-disable-next-line no-console
    console.warn('[validateFileType] Sentry reporting failed:', e);
  }
};

export const validateFileType = (
  fileType: string,
  accept: string,
  fileName?: string
): boolean => {
  if (!accept || accept === '') {
    return false; // デフォルト拒否（フェイルセーフ）
  }

  // RFC 2045 §5.1 に従い MIME は case-insensitive で比較する
  // ブラウザ実装によっては application/vnd.ms-excel.sheet.macroenabled.12 のように
  // 全小文字で返すケースがあるため、両辺を lowercase に正規化して比較する
  const acceptedTypes = accept.split(',').map((s) => s.trim().toLowerCase());
  const fileTypeLower = fileType.toLowerCase();

  // 通常パス: file.type が有効かつ accept に含まれる
  if (fileTypeLower && acceptedTypes.includes(fileTypeLower)) {
    return true;
  }

  // フォールバック: fileType が空の場合のみ（ブラウザがOS未対応で判定できなかったケース）
  // fileName の拡張子から推定MIMEを引き、accept と合致すれば許可
  // fileType に値がある場合は MIME 偽装攻撃を防ぐためフォールバックしない
  const extension = fileName ? fileName.split('.').pop()?.toLowerCase() ?? '' : '';
  const candidates = EXTENSION_FALLBACK_MIMES[extension];
  const fallbackMatched = !fileType && !!candidates && candidates.some((m) => acceptedTypes.includes(m));

  // Sentry 観測: フォールバック発火 / 完全拒否 の両ケースを記録
  reportValidateFileTypeFallback({
    fileType,
    fileName,
    extension,
    accept,
    fallbackMatched
  });

  return fallbackMatched;
};
